import { useEffect, useRef, useState } from 'react'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { SeatMeter } from './SeatMeter'
import {
  SEAT_PLAN, SEAT_LADDER, canPurchaseSeats, formatSeatPrice, quoteSeatsCents, ladderTierFor,
} from '../../lib/seatPlan'
import { FLAT_PRICING } from '../../lib/flatPricing'
import { isNativeShell } from '../../mobile/lib/appMode'
import type { OrgKycStatus, OrgSeatsLite } from '../../lib/orgAccess'

/**
 * SeatPaywall — the sheet body. "The person, not the plan."
 *
 * ── WHEN IT OPENS ─────────────────────────────────────────────────────────
 * At the MOMENT OF INTENT and nowhere else: `POST /org/invites` answered
 * **402**, carrying the whole `seats` block, so this opens with no second
 * round trip and with the email the owner just typed. Never a nag on load.
 * (There is one second, deliberate entry: the `--next` pip in the meter, for
 * an owner who is shopping rather than acting — reaching the price without
 * first triggering a failed invite.)
 *
 * The person who gets here has ALREADY decided they want the colleague. The
 * purchase decision is 90% made. This sheet's job is not to sell — it is to
 * (a) not lose them, (b) make the price legible enough that they don't leave
 * to check whether it's a trap, and (c) put them back exactly where they were.
 * Anything that makes them do work they did not come to do is a downgrade
 * dressed as delight; that is why there is no dial here, and why the default
 * quantity is already correct.
 *
 * ── "CHEAPER THAN ANY COMPETITOR", MADE LEGIBLE WITHOUT A CLAIM ───────────
 * The per-person line — the monthly total divided by the seats it covers — is
 * the entire pitch in four words, updates as the team grows, names nobody, and
 * is true. A comparison table would be a commercial claim we must keep current
 * and defensible while counsel has not cleared the terms (#29).
 *
 * ── THE CTA BRANCH IS THE DANGEROUS PART ──────────────────────────────────
 * `canPurchaseSeats()` is the ONLY predicate a purchase control branches on —
 * never the plan's armed flag on its own, which is true inside the app too and
 * is exactly how the button appears where it must not. Seats are web-only for
 * v1; a
 * routine hosting deploy must never drop a purchase CTA (or an outbound link
 * to one, which is the same IAP violation with extra steps) into the reviewed
 * App Store build. Every other branch ends in a CONTACT PATH, never a disabled
 * button with no explanation. See `lib/seatPlan.ts` for the predicate itself.
 */

export type PaywallCta =
  | { kind: 'buy' }
  | { kind: 'native' }
  | { kind: 'inert'; message: string }
  | { kind: 'bank_transfer' }
  | { kind: 'ask_owner' }

/**
 * Pure, and unit-tested against a table — this decides whether a button that
 * spends money is rendered.
 *
 * Order matters. Native beats everything (a store policy, not a preference).
 * Then role: `POST /billing/seats/checkout-session` is OWNER-only, so an admin
 * pressing a purchase button gets a 403 they cannot fix. Then India, which has
 * no card rail at all (`DEFAULT_MATRIX.IN` is bank-only and there is no
 * recurring bank product) — rendering Checkout there is a guaranteed decline.
 * Then the rail's own armed state.
 */
export function paywallCta(a: {
  viewerOrgRole: 'owner' | 'admin' | 'member' | null
  billingCountryCode: string | null
  /** `{configured:false}` from `GET /billing/seats`, when we have it. */
  configured: boolean | null
  serverMessage: string | null
  canPurchase: boolean
  native: boolean
}): PaywallCta {
  if (a.native) return { kind: 'native' }
  if (a.viewerOrgRole === 'admin') return { kind: 'ask_owner' }
  if ((a.billingCountryCode || '').toUpperCase() === 'IN') return { kind: 'bank_transfer' }
  if (!a.canPurchase || a.configured === false) {
    return {
      kind: 'inert',
      message: a.serverMessage
        ?? 'Seat purchases aren’t switched on yet. We’ll email your organization’s owner the moment they are.',
    }
  }
  return { kind: 'buy' }
}

const SUPPORT_EMAIL = 'info@thediaex.com'

function mailtoFor(a: { orgName: string; seats: number; subject: string; to?: string }): string {
  const body = [
    `Company: ${a.orgName}`,
    `Seats needed: ${a.seats}`,
    '',
    'Please add the seats to our Diaex account.',
  ].join('\n')
  return `mailto:${a.to ?? SUPPORT_EMAIL}?subject=${encodeURIComponent(a.subject)}&body=${encodeURIComponent(body)}`
}

export function SeatPaywall({
  seats,
  orgName,
  orgKyc,
  inviteEmail,
  viewerOrgRole,
  billingCountryCode,
  configured,
  serverMessage,
  ownerEmail,
  busy,
  error,
  sharedMeter = true,
  onBuy,
  onDismiss,
}: {
  seats: OrgSeatsLite
  orgName: string
  orgKyc: OrgKycStatus
  /** The address the owner typed. Carried through Stripe and back. */
  inviteEmail: string
  viewerOrgRole: 'owner' | 'admin' | 'member' | null
  billingCountryCode: string | null
  configured: boolean | null
  serverMessage: string | null
  ownerEmail: string | null
  busy: boolean
  error: string | null
  /** False when the header meter is scrolled out of view — a shared-element
   *  flight from an off-screen origin reads as broken, not as magic. */
  sharedMeter?: boolean
  onBuy: (totalSeats: number) => void
  onDismiss: () => void
}) {
  // The default is ALREADY CORRECT: exactly the seats needed to send this
  // invite. The stepper is optional furniture for an owner buying headroom —
  // if it ever becomes the primary control we have rebuilt the dial this
  // design rejected.
  const needed = Math.max(seats.limit + 1, seats.used + seats.pendingInvites + 1)
  const [total, setTotal] = useState(needed)
  // Re-seed only when the requirement genuinely moves (a second invite queued
  // while the sheet is open). Doing this in an effect would fire a state update
  // on every mount for no change at all.
  const seededFor = useRef(needed)
  if (seededFor.current !== needed) { seededFor.current = needed; setTotal(needed) }

  // Re-render when `/api/health` re-points the plan mid-session.
  const [, bump] = useState(0)
  useEffect(() => {
    const onPlan = () => bump((n) => n + 1)
    window.addEventListener('aska:seat-plan-changed', onPlan)
    return () => window.removeEventListener('aska:seat-plan-changed', onPlan)
  }, [])

  const cta = paywallCta({
    viewerOrgRole,
    billingCountryCode,
    configured,
    serverMessage,
    canPurchase: canPurchaseSeats(),
    native: isNativeShell(),
  })

  // FLAT PRICING: the tiered ladder (Aska+/Team/Max) replaces the legacy
  // flat-plan arithmetic — DISPLAY only; the CTA branch below is untouched.
  const flat = FLAT_PRICING.live
  const ladderTier = ladderTierFor(total)
  const monthlyCents = flat ? ladderTier.priceCents : quoteSeatsCents(total)
  const perPersonCents = total > 0 ? Math.round(monthlyCents / total) : 0
  const firstName = inviteEmail.split('@')[0]?.replace(/[._-]+/g, ' ').trim() || 'them'
  const displayName = firstName.charAt(0).toUpperCase() + firstName.slice(1)

  const step = (delta: number) => {
    setTotal((t) => Math.max(needed, Math.min(500, t + delta)))
    feedback.select()
  }

  return (
    <div data-testid="seat-paywall">
      {/* The meter flies in from the header and becomes the hero. */}
      <div className="pb-1 pt-1">
        <SeatMeter
          used={seats.used}
          pendingInvites={seats.pendingInvites}
          limit={seats.limit}
          orgKyc={orgKyc}
          orgName={orgName}
          activeCount={seats.used}
          shared={sharedMeter}
          compact
        />
      </div>

      <p className="mt-4 text-sm text-soft">Add a seat for</p>
      <p className="truncate text-lg font-medium text-ink" data-testid="seat-paywall-invitee">
        {inviteEmail || 'your next teammate'}
      </p>

      <p className="mt-4 font-serif text-3xl text-ink">
        <span className="tabular" data-testid="seat-paywall-price">{formatSeatPrice(monthlyCents)}</span>
        <span className="ml-1 font-sans text-sm text-soft">/ month</span>
      </p>
      <p className="mt-1 text-sm text-ink">
        {flat
          ? `${ladderTier.name} — covers your whole team, up to ${ladderTier.seatCap} people.`
          : `Covers your whole team, up to ${SEAT_PLAN.flatUpTo} people.`}
      </p>
      <p className="mt-0.5 text-sm text-soft">
        That’s <span className="tabular">{formatSeatPrice(perPersonCents)}</span> per person today.
      </p>

      {/* Optional furniture. Deliberately secondary — the default is right. */}
      <div className="mt-3 flex items-center gap-2">
        <PressScale
          feel="tap"
          onClick={() => step(-1)}
          disabled={total <= needed}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-lg text-ink disabled:opacity-40 focus-visible:shadow-[0_0_0_3px_var(--gold-300)]"
          aria-label="One less seat"
          data-testid="seat-paywall-minus"
        >
          −
        </PressScale>
        <span
          className="tabular min-w-[3ch] text-center text-sm font-semibold text-ink"
          aria-live="polite"
          aria-label={`${total} seats total`}
          data-testid="seat-paywall-total"
        >
          {total}
        </span>
        <PressScale
          feel="tap"
          onClick={() => step(1)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-lg text-ink focus-visible:shadow-[0_0_0_3px_var(--gold-300)]"
          aria-label="One more seat"
          data-testid="seat-paywall-plus"
        >
          +
        </PressScale>
        <span className="text-[12.5px] text-soft">seats in total</span>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-ink">
        {displayName} sees your live prices and your suppliers’ direct contacts the moment they
        join — they inherit {orgName}’s verification, with no review of their own.
      </p>

      {error && (
        <p className="mt-3 text-[13px] font-medium text-danger" role="alert" data-testid="seat-paywall-error">
          {error}
        </p>
      )}

      {/* ── the branch ─────────────────────────────────────────────────── */}
      <div className="mt-5 space-y-2">
        {cta.kind === 'buy' && (
          <PressScale
            feel="tap"
            disabled={busy}
            onClick={() => onBuy(total)}
            className="btn-primary h-11 w-full"
            data-testid="seat-paywall-buy"
          >
            {busy ? 'Opening checkout…' : `Add a seat & send ${displayName}’s invite`}
          </PressScale>
        )}

        {cta.kind === 'native' && (
          <p
            className="rounded-md border border-line bg-cream px-3 py-3 text-[13px] leading-relaxed text-ink"
            data-testid="seat-paywall-native"
          >
            Seats are managed on the web. Open thediaex.com/account/team on a computer to add one.
          </p>
        )}

        {cta.kind === 'inert' && (
          <>
            <p className="text-[13px] leading-relaxed text-soft" data-testid="seat-paywall-inert">
              {cta.message}
            </p>
            <a
              href={mailtoFor({ orgName, seats: total, subject: `Diaex — add seats for ${orgName}` })}
              className="btn-secondary flex h-11 w-full items-center justify-center"
              data-testid="seat-paywall-contact"
            >
              Ask us to add a seat
            </a>
          </>
        )}

        {cta.kind === 'bank_transfer' && (
          <>
            <p className="text-[13px] leading-relaxed text-soft" data-testid="seat-paywall-bank">
              Cards aren’t available for Indian businesses on Diaex. We’ll invoice you and add
              the seats.
            </p>
            <a
              href={mailtoFor({ orgName, seats: total, subject: `Diaex — add seats by bank transfer (${orgName})` })}
              className="btn-secondary flex h-11 w-full items-center justify-center"
              data-testid="seat-paywall-contact"
            >
              Add seats by bank transfer
            </a>
          </>
        )}

        {cta.kind === 'ask_owner' && (
          <>
            <p className="text-[13px] leading-relaxed text-soft" data-testid="seat-paywall-admin">
              Only the owner of {orgName} can add seats.
            </p>
            <a
              href={mailtoFor({
                orgName, seats: total, to: ownerEmail ?? undefined,
                subject: `Diaex — can you add a seat for ${inviteEmail || 'a teammate'}?`,
              })}
              className="btn-secondary flex h-11 w-full items-center justify-center"
              data-testid="seat-paywall-contact"
            >
              Ask the owner to add a seat
            </a>
          </>
        )}

        {/* ALWAYS rendered, always enabled, always returns to a fully working
            team screen with the typed email intact. Cancelling is not a
            failure and is not styled like one. */}
        <PressScale
          feel="dim"
          onClick={onDismiss}
          className="btn-ghost h-11 w-full"
          data-testid="seat-paywall-dismiss"
        >
          Not now
        </PressScale>
      </div>

      {/* Never behind a "see details" link: this is what a keyboard or
          screen-reader user reads INSTEAD of a control. */}
      <div className="mt-5 border-t border-line pt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">
          What seats cost
        </p>
        {flat ? (
          /* The LADDER (docs/PRICING-MODEL.md §2): browse seats free, then a
             tier sized to the team. Per-seat falls as the org grows — the
             number that makes it legible as cheap. */
          <table className="w-full text-[13px] text-ink" data-testid="seat-ladder-table">
            <tbody>
              <tr>
                <th scope="row" className="py-1 text-left font-normal">Browse seats (1–{SEAT_PLAN.includedFree})</th>
                <td className="py-1 text-right tabular">free</td>
              </tr>
              {SEAT_LADDER.map((t) => (
                <tr key={t.key}>
                  <th scope="row" className="py-1 text-left font-normal">{t.name} — up to {t.seatCap} people</th>
                  <td className="py-1 text-right tabular">
                    {formatSeatPrice(t.priceCents)}/mo · {formatSeatPrice(Math.round(t.priceCents / t.seatCap))}/seat
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-[13px] text-ink">
            <tbody>
              <tr>
                <th scope="row" className="py-1 text-left font-normal">1–{SEAT_PLAN.includedFree} people</th>
                <td className="py-1 text-right tabular">free</td>
              </tr>
              <tr>
                <th scope="row" className="py-1 text-left font-normal">{SEAT_PLAN.includedFree + 1}–{SEAT_PLAN.flatUpTo} people</th>
                <td className="py-1 text-right tabular">{formatSeatPrice(SEAT_PLAN.flatCents)}/mo total</td>
              </tr>
              <tr>
                <th scope="row" className="py-1 text-left font-normal">{SEAT_PLAN.flatUpTo + 1}th onward</th>
                <td className="py-1 text-right tabular">+{formatSeatPrice(SEAT_PLAN.extraSeatCents)}/mo each</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
